/*
 * assets/status-alert-bar.js : the Status Alert Bar's rotation, dismissal and theme-editor behaviour
 * (sections/status-alert-bar.liquid).
 *
 * THE DEFINITION IS GUARDED because the section renders its own script tag per placement and the header group
 * carries two placements : evaluating a top-level class twice in one page throws before any bar is wired, so the
 * second evaluation has to be a no-op, the way the stock assets guard theirs (assets/localization-form.js and
 * siblings). Section audit, 2026-09-11.
 *
 * STORAGE IS WRAPPED because a browser that blocks site data throws on sessionStorage, and an unguarded read in
 * the constructor aborted the whole set-up (arrows, swipe, rotation and the severity role) ; a throw means "not
 * stored", so the bar simply shows again on the next page. Section audit, 2026-09-11.
 *
 * ESCAPE DISMISSES ONLY WHEN THE DISMISS CONTROL IS RENDERED, so "Show Dismiss Button" governs every way of
 * dismissing : a bar the merchant configured as non-dismissable used to vanish for the session when a keyboard
 * user pressed Escape on its link. Section audit, 2026-09-11.
 *
 * THEME EDITOR : the editor re-renders the section after every setting change, so the document listeners are
 * added on connect and removed on disconnect, and the rotation stops on disconnect ; otherwise the old element's
 * listeners and interval outlive it. Only the first alert renders until the rotation moves, so shopify:block:select
 * brings the selected alert into view and pauses the rotation while it is edited ; deselect resumes it.
 * Section audit, 2026-09-11.
 */
if (!customElements.get('status-alert-bar')) {
  const readStored = (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const writeStored = (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      // Not stored : the bar shows again on the next page.
    }
  };
  const clearStored = (key) => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      // Nothing was stored, so there is nothing to clear.
    }
  };

  customElements.define(
    'status-alert-bar',
    class StatusAlertBar extends HTMLElement {
      constructor() {
        super();

        this.inner = this.querySelector('.status-alert-bar__inner');
        this.sectionId = this.dataset.sectionId;
        this.storageKey = `status-alert-bar-dismissed-${this.sectionId}`;
        this.content = this.querySelector('.status-alert-bar__content');
        this.slides = Array.from(this.querySelectorAll('.status-alert-bar__slide'));
        this.currentIndex = 0;
        this.autoplayInterval = null;
        this.transitioning = false;
        // A dismissed bar never rotates again until the editor shows it : the pointer-leave and focus-out
        // handlers below restart the rotation, and used to do so on the hidden bar.
        this.dismissed = false;
        this.autoplayEnabled = this.slides.length > 1 && this.dataset.autoplay === 'true';

        // Dismiss on close button click
        this.querySelectorAll('.status-alert-bar__close').forEach((btn) =>
          btn.addEventListener('click', () => this.dismiss())
        );

        // Dismiss on Escape key, only where the dismiss control exists (see the header)
        if (this.querySelector('.status-alert-bar__close')) {
          this.addEventListener('keyup', (evt) => {
            if (evt.code === 'Escape') this.dismiss();
          });
        }

        // If previously dismissed this session, hide immediately and do not rotate the hidden bar
        if (readStored(this.storageKey) === 'true') {
          this.classList.add('status-alert-bar--hidden');
          this.dismissed = true;
        }

        // Carousel nav buttons
        const prevBtn = this.querySelector('.status-alert-bar__nav--prev');
        const nextBtn = this.querySelector('.status-alert-bar__nav--next');
        if (prevBtn) prevBtn.addEventListener('click', () => this.prev());
        if (nextBtn) nextBtn.addEventListener('click', () => this.next());

        // Touch swipe support
        if (this.slides.length > 1) {
          this.touchStartX = 0;
          this.addEventListener('touchstart', (evt) => {
            this.touchStartX = evt.changedTouches[0].screenX;
          }, { passive: true });
          this.addEventListener('touchend', (evt) => {
            const delta = evt.changedTouches[0].screenX - this.touchStartX;
            if (Math.abs(delta) > 50) {
              delta < 0 ? this.next() : this.prev();
            }
          }, { passive: true });
        }

        // Auto-rotate, paused while the pointer or the keyboard focus is inside the bar
        if (this.autoplayEnabled) {
          this.startAutoplay();
          this.addEventListener('mouseenter', () => this.stopAutoplay());
          this.addEventListener('mouseleave', () => this.startAutoplay());
          this.addEventListener('focusin', () => this.stopAutoplay());
          this.addEventListener('focusout', () => this.startAutoplay());
        }

        // Set initial severity from first slide
        this.updateSeverity();

        // Theme editor handlers, kept on the instance so disconnectedCallback can remove the same functions.
        // The slide carries block.shopify_attributes, so the block event's target is the slide itself.
        this.onSectionSelect = (evt) => {
          if (evt.detail.sectionId === this.sectionId) this.show();
        };
        this.onBlockSelect = (evt) => {
          if (evt.detail.sectionId !== this.sectionId) return;
          const index = this.slides.indexOf(evt.target.closest('.status-alert-bar__slide'));
          this.show();
          this.stopAutoplay();
          if (index >= 0 && index !== this.currentIndex) this.swapSlide(index);
        };
        this.onBlockDeselect = (evt) => {
          if (evt.detail.sectionId !== this.sectionId) return;
          if (this.autoplayEnabled) this.startAutoplay();
        };
      }

      connectedCallback() {
        if (!Shopify.designMode) return;
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:block:select', this.onBlockSelect);
        document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      disconnectedCallback() {
        this.stopAutoplay();
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      }

      goTo(index) {
        if (this.slides.length === 0 || this.transitioning) return;

        const nextIndex = (index + this.slides.length) % this.slides.length;
        if (nextIndex === this.currentIndex) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion || !this.content) {
          this.swapSlide(nextIndex);
          return;
        }

        this.transitioning = true;
        this.content.classList.add('status-alert-bar__content--fading');

        this.content.addEventListener(
          'transitionend',
          () => {
            this.swapSlide(nextIndex);
            this.content.classList.remove('status-alert-bar__content--fading');
            this.content.addEventListener('transitionend', () => { this.transitioning = false; }, { once: true });
          },
          { once: true }
        );
      }

      swapSlide(nextIndex) {
        this.slides[this.currentIndex].classList.remove('status-alert-bar__slide--active');
        this.slides[this.currentIndex].setAttribute('aria-hidden', 'true');

        this.currentIndex = nextIndex;

        this.slides[this.currentIndex].classList.add('status-alert-bar__slide--active');
        this.slides[this.currentIndex].removeAttribute('aria-hidden');

        this.updateSeverity();
      }

      prev() {
        this.goTo(this.currentIndex - 1);
      }

      next() {
        this.goTo(this.currentIndex + 1);
      }

      startAutoplay() {
        this.stopAutoplay();
        if (this.dismissed) return;
        const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
        this.autoplayInterval = setInterval(() => this.next(), speed);
      }

      stopAutoplay() {
        if (this.autoplayInterval) {
          clearInterval(this.autoplayInterval);
          this.autoplayInterval = null;
        }
      }

      updateSeverity() {
        if (!this.inner || this.slides.length === 0) return;

        const activeSlide = this.slides[this.currentIndex];
        const severity = activeSlide ? activeSlide.dataset.severity || 'info' : 'info';

        this.inner.classList.remove('status-alert-bar--info', 'status-alert-bar--warning', 'status-alert-bar--critical');
        this.inner.classList.add(`status-alert-bar--${severity}`);
        this.inner.setAttribute('role', severity === 'info' ? 'status' : 'alert');
      }

      dismiss() {
        if (!this.inner) return;

        this.dismissed = true;
        this.stopAutoplay();

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (prefersReducedMotion) {
          this.classList.add('status-alert-bar--hidden');
          writeStored(this.storageKey, 'true');
          return;
        }

        this.inner.classList.add('status-alert-bar--dismissing');
        this.inner.addEventListener(
          'transitionend',
          () => {
            this.classList.add('status-alert-bar--hidden');
            writeStored(this.storageKey, 'true');
          },
          { once: true }
        );
      }

      // Theme editor only : re-show the bar when its section or one of its alerts is selected, and resume the
      // rotation it lost on dismissal so the preview matches the storefront.
      show() {
        clearStored(this.storageKey);
        this.dismissed = false;
        this.classList.remove('status-alert-bar--hidden');
        if (this.inner) {
          this.inner.classList.remove('status-alert-bar--dismissing');
        }
        if (this.autoplayEnabled) this.startAutoplay();
      }
    }
  );
}
